import { useState, useContext } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import { ThemeContext } from "styled-components";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";
import Router from "next/router";

import Modal from "../common/Modal";
import Icon from "../common/Icon";
import Button from "../common/Button";
import Accordion from "../common/Accordion";
import { trackEvent } from "../../lib/event-tracking";
import { pushRoute } from "../../lib/routing";
import { useIsMobile } from "../../lib/media";

interface Props {
  name: string;
  address: string;
  id: string;
  title: string;
  queryParams?: Record<string, any>;
  isAvailable: boolean;
  modalView?: boolean;
  city: string;
}

export const Benefits = () => {
  const { t } = useTranslation("sitterprofile");

  const benefitIcons = [
    "vet",
    "cancellation",
    "vetted",
    "star-off",
    "support",
    "secure",
  ];
  const benefits = [...Array(6)].map((_, i) => {
    return {
      icon: benefitIcons[i],
      title: t(`benefit${i + 1}Title`),
      description: t(`benefit${i + 1}Desc`)
    };
  });

  return (
    <Box>
      <Text as="h2" variant="text.h3" mb={2}>
        {t("gudogBenefits")}
      </Text>

      <Accordion items={benefits} />
    </Box>
  );
};

const Contact = (props: Props) => {
  const theme = useContext(ThemeContext);

  const [isBenefitsModalVisible, setIsBenefitsModalVisible] = useState(false);
  const openBenefitsModal = () => setIsBenefitsModalVisible(true);
  const closeBenefitsModal = () => setIsBenefitsModalVisible(false);

  const isMobileViewport = useIsMobile();

  const contactSitter = () => {
    trackEvent("owner_sitter_contact_intended", {
      sitterId: props.id
    });

    // Redirect to rover
    const encodedCity = encodeURIComponent(props.city);
    const baseUrl = `https://www.rover.com/search/?location=${encodedCity}`;
    const utmParams =
      "utm_source=gudog&utm_medium=super_affiliates&utm_campaign=609140897&utm_term=&utm_content=";

    const destinationUrl = `${baseUrl}${
      baseUrl.includes("?") ? "&" : "?"
    }${utmParams}`;

    Router.push(destinationUrl);
  };

  return (
    <Box
      sx={{
        top: ["initial", props.modalView ? "24px" : "155px"],
        right: 0,
        bottom: 0,
        left: 0,
        position: ["fixed", "sticky"],
        zIndex: 1
      }}
    >
      <Box
        p={[2, 3]}
        sx={{
          zIndex: 1,
          border: ["none", `1px solid ${theme["colors"].gray}`],
          borderTop: `1px solid ${theme["colors"].gray}`,
          borderRadius: [0, "default"],
          backgroundColor: `${theme["colors"].samoyedWhite}`
        }}
      >
        <Box>
          {!isMobileViewport && (
            <Text
              as="p"
              sx={{
                color: "veryDarkGray",
                fontSize: 2,
                marginBottom: ["8px", "10px"],
                fontWeight: "bold"
              }}
            >
              {props.name}
            </Text>
          )}

          {!isMobileViewport && props?.address && (
            <Flex
              pb={1}
              mb={1}
              sx={{
                alignItems: "center",
                "> svg": { flexShrink: 0 }
              }}
            >
              <Icon name="marker" color="aquamarine" />
              <Text
                as="span"
                sx={{
                  color: "veryDarkGray",
                  fontSize: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginLeft: "2px"
                }}
              >
                {props.address}
              </Text>
            </Flex>
          )}
        </Box>

        <Button
          disabled={!props.isAvailable}
          variant="primary"
          size="large"
          width={1}
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden"
          }}
          onClick={contactSitter}
          data-test="contact"
        >
          {props.isAvailable ? (
            <Trans
              i18nKey="sitterprofile:contact"
              values={{ name: props.name }}
            />
          ) : (
            <Trans i18nKey="sitterprofile:unavailableContact" />
          )}
        </Button>

        {!props.isAvailable ? (
          <Text
            as="p"
            sx={{
              color: "darkGray",
              fontSize: 2,
              marginTop: ["8px", "10px"],
              fontWeight: "regular"
            }}
          >
            <Trans
              i18nKey="sitterprofile:unavailableSitter"
              values={{ name: props.name }}
            />
          </Text>
        ) : (
          isMobileViewport && (
            <>
              <Text
                as="p"
                mt={2}
                fontSize={2}
                fontWeight="600"
                textAlign="center"
                color="springGreen"
                onClick={openBenefitsModal}
              >
                <Trans
                  i18nKey="common:gudogBenefitsShort"
                  components={{ u: <u /> }}
                />
              </Text>
              <Modal
                open={isBenefitsModalVisible}
                size={"full"}
                onClose={closeBenefitsModal}
                onOutsideClick={closeBenefitsModal}
              >
                <Box py={4}>
                  {closeBenefitsModal && (
                    <Box sx={{ position: "absolute", top: 24, right: 0 }}>
                      <Button
                        variant="link"
                        onClick={() => closeBenefitsModal()}
                      >
                        <Icon name="close" />
                      </Button>
                    </Box>
                  )}
                  <Benefits />
                </Box>
              </Modal>
            </>
          )
        )}
      </Box>

      {props.isAvailable && !isMobileViewport && (
        <Box
          mt={3}
          pt={3}
          pb={1}
          px={3}
          sx={{
            zIndex: 1,
            border: ["none", `1px solid ${theme["colors"].gray}`],
            borderTop: `1px solid ${theme["colors"].gray}`,
            borderRadius: [0, "default"],
            backgroundColor: `${theme["colors"].samoyedWhite}`
          }}
        >
          <Benefits />
        </Box>
      )}
    </Box>
  );
};

Contact.defaultProps = {
  queryParams: null
};

export default Contact;
